import React from 'react';
import type Stripe from 'stripe';
import useUser from '../global/useUser';
import useSocket from '../socket/useSocket';

export interface CompleteData {
  paymentId: string;
  appointmentId: string | undefined;
  orderId: string | undefined;
  customerId: string | null;
}

export interface FailureData {
  failureCode: string | null;
  failureMessage: string | null;
  status: Stripe.Terminal.Reader.Action.Status;
  type: Stripe.Terminal.Reader.Action.Type;
  appointmentId: string | undefined;
  orderId: string | undefined;
  paymentIntentId: string;
  paymentId: string;
}

export interface CapturableData {
  paymentId: string;
}

interface Params {
  onCheckoutCompleted?: (data: CompleteData) => void | Promise<void>;
  onCheckoutFailed?: (data: FailureData) => void | Promise<void>;
  onCheckoutUpdated?: (data: CompleteData) => void | Promise<void>;
  onCheckoutCapturable?: (data: CapturableData) => void | Promise<void>;
  enabled?: boolean;
}

function useCheckoutSocket({
  onCheckoutCompleted,
  onCheckoutFailed,
  onCheckoutUpdated,
  onCheckoutCapturable,
  enabled = true,
}: Params) {
  const socket = useSocket();
  const { user: loadedUser } = useUser();

  const onCompleteRef = React.useRef(onCheckoutCompleted);
  const onFailureRef = React.useRef(onCheckoutFailed);
  const onUpdatedRef = React.useRef(onCheckoutUpdated);
  const onCapturableRef = React.useRef(onCheckoutCapturable);
  React.useEffect(() => {
    onCompleteRef.current = onCheckoutCompleted;
  }, [onCheckoutCompleted]);

  React.useEffect(() => {
    onFailureRef.current = onCheckoutFailed;
  }, [onCheckoutFailed]);

  React.useEffect(() => {
    onUpdatedRef.current = onCheckoutUpdated;
  }, [onCheckoutUpdated]);

  React.useEffect(() => {
    onCapturableRef.current = onCheckoutCapturable;
  }, [onCheckoutCapturable]);

  React.useEffect(() => {
    if (!enabled) return undefined;

    const onSuccess = async (data: CompleteData) => {
      try {
        onCompleteRef.current?.(data);
      } catch {
        /* empty */
      }
    };

    const onError = async (data: FailureData) => {
      try {
        onFailureRef.current?.(data);
      } catch {
        /* empty */
      }
    };

    const onUpdate = async (data: CompleteData) => {
      try {
        onUpdatedRef.current?.(data);
      } catch {
        /* empty */
      }
    };

    const onCapturable = async (data: CapturableData) => {
      try {
        onCapturableRef.current?.(data);
      } catch {
        /* empty */
      }
    };

    socket.on('checkoutCompleted', onSuccess);
    socket.on('checkoutFailed', onError);
    socket.on('checkoutUpdated', onUpdate);
    socket.on('checkoutCapturable', onCapturable);

    return () => {
      socket.off('checkoutCompleted', onSuccess);
      socket.off('checkoutFailed', onError);
      socket.off('checkoutUpdated', onUpdate);
      socket.off('checkoutCapturable', onCapturable);
    };
  }, [socket, loadedUser?.business.id, enabled]);
}

export default useCheckoutSocket;
